'use client';

import { useCallback, useMemo, useState } from 'react';
import { reverseGeocode } from '@/lib/google-places/reverse-geocode';
import { Loader2, MapPin, Navigation } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useGoogleMapsAPI } from '@kit/artists/hooks/use-google-maps';
import { Autocomplete, type AutocompleteOption } from '@kit/ui/autocomplete';
import { Button } from '@kit/ui/button';
import { toast } from '@kit/ui/sonner';

import { useBrowserGeolocation } from '../_hooks/use-browser-geolocation';

// ============================================================================
// Types
// ============================================================================

export interface CityAutocompleteResult {
  city: string;
  country: string;
  countryCode: string;
  placeId: string;
  latitude: number | null;
  longitude: number | null;
}

interface LocationAutocompleteProps {
  value?: CityAutocompleteResult | null;
  onChange: (location: CityAutocompleteResult | null) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
}

// Types for the new Google Places API (AutocompleteSuggestion + Place)
interface PlacePrediction {
  placeId: string;
  text: { text: string };
  mainText: { text: string };
  secondaryText?: { text: string };
}

interface AutocompleteSuggestion {
  placePrediction?: PlacePrediction;
}

// Google Maps Places API types (loaded dynamically via script tag)
interface GoogleMapsPlaces {
  AutocompleteSuggestion?: {
    fetchAutocompleteSuggestions: (opts: {
      input: string;
      includedPrimaryTypes: string[];
    }) => Promise<{ suggestions: AutocompleteSuggestion[] }>;
  };
  Place?: new (opts: { id: string }) => {
    fetchFields: (opts: { fields: string[] }) => Promise<void>;
    displayName?: string;
    location?: { lat: () => number; lng: () => number };
    addressComponents?: Array<{
      types: string[];
      longText?: string;
      shortText?: string;
    }>;
  };
}

interface GoogleMapsWindow {
  google?: {
    maps?: {
      places?: GoogleMapsPlaces;
    };
  };
}

// ============================================================================
// Component
// ============================================================================

/**
 * City autocomplete component using the new Google Places API
 * (AutocompleteSuggestion + Place class, replacing deprecated AutocompleteService + PlacesService)
 *
 * Built on top of the @kit/ui/autocomplete component for accessibility and mobile support.
 * Restricted to cities only.
 */
export function LocationAutocomplete({
  value,
  onChange,
  label,
  placeholder,
  error,
  required,
}: LocationAutocompleteProps) {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('home:locationAutocomplete.defaultLabel');
  const resolvedPlaceholder =
    placeholder ?? t('home:locationAutocomplete.defaultPlaceholder');
  // State
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([]);
  const [isLoadingPredictions, setIsLoadingPredictions] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Browser geolocation
  const {
    getCurrentLocation,
    getIpLocation,
    isLoading: isGeolocating,
    isSupported,
  } = useBrowserGeolocation();

  // Get Google Maps API key from env
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY || '';

  // Load Google Maps API
  const {
    loading: isLoadingApi,
    error: apiError,
    isLoaded,
  } = useGoogleMapsAPI({
    apiKey,
    libraries: ['places'],
  });

  // Convert suggestions to AutocompleteOption format
  const options: AutocompleteOption[] = useMemo(() => {
    return suggestions.map((prediction) => ({
      value: prediction.placeId,
      label: prediction.mainText.text,
      description: prediction.secondaryText?.text,
      icon: <MapPin className="text-muted-foreground h-4 w-4" />,
    }));
  }, [suggestions]);

  // Handle input change - fetch suggestions using the new Places API
  const handleInputChange = useCallback(
    async (inputValue: string) => {
      if (!inputValue || inputValue.length < 2) {
        setSuggestions([]);
        return;
      }

      if (!isLoaded) {
        return;
      }

      const google = (window as unknown as GoogleMapsWindow).google;
      if (!google?.maps?.places?.AutocompleteSuggestion) {
        return;
      }

      setIsLoadingPredictions(true);

      try {
        const response =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            {
              input: inputValue,
              includedPrimaryTypes: ['locality'],
            },
          );

        const placePredictions: PlacePrediction[] = (
          response.suggestions as AutocompleteSuggestion[]
        )
          .filter(
            (s: AutocompleteSuggestion) => s.placePrediction !== undefined,
          )
          .map((s: AutocompleteSuggestion) => s.placePrediction!);

        setSuggestions(placePredictions);
      } catch {
        setSuggestions([]);
      } finally {
        setIsLoadingPredictions(false);
      }
    },
    [isLoaded],
  );

  // Handle selection - fetch place details using the new Place class and call onChange
  const handleSelect = useCallback(
    async (selectedValue: string | string[] | null) => {
      // Handle clear
      if (!selectedValue) {
        onChange(null);
        setSuggestions([]);
        return;
      }

      // Get the place_id (single select only)
      const placeId = Array.isArray(selectedValue)
        ? selectedValue[0]
        : selectedValue;

      if (!placeId) {
        onChange(null);
        return;
      }

      // Find the original suggestion for fallback city name
      const suggestion = suggestions.find((s) => s.placeId === placeId);

      const google = (window as unknown as GoogleMapsWindow).google;
      if (!google?.maps?.places?.Place) {
        return;
      }

      setIsLoadingDetails(true);

      try {
        const place = new google.maps.places.Place({ id: placeId });
        await place.fetchFields({
          fields: ['displayName', 'location', 'addressComponents'],
        });

        // Extract city and country from address components
        let city = suggestion?.mainText.text ?? place.displayName ?? '';
        let country = '';
        let countryCode = '';

        if (place.addressComponents) {
          for (const component of place.addressComponents) {
            if (component.types.includes('locality')) {
              city = component.longText ?? city;
            }
            if (component.types.includes('country')) {
              country = component.longText ?? '';
              countryCode = component.shortText ?? '';
            }
          }
        }

        // Extract coordinates from location
        const latitude = place.location?.lat() ?? null;
        const longitude = place.location?.lng() ?? null;

        const result: CityAutocompleteResult = {
          city,
          country,
          countryCode,
          placeId,
          latitude,
          longitude,
        };

        onChange(result);
        // Clear suggestions after selection
        setSuggestions([]);
      } catch {
        // If Place.fetchFields fails, use suggestion data as fallback
        if (suggestion) {
          const result: CityAutocompleteResult = {
            city: suggestion.mainText.text,
            country: suggestion.secondaryText?.text ?? '',
            countryCode: '',
            placeId,
            latitude: null,
            longitude: null,
          };
          onChange(result);
          setSuggestions([]);
        }
      } finally {
        setIsLoadingDetails(false);
      }
    },
    [suggestions, onChange],
  );

  // Compute the display value for the autocomplete
  const displayValue = useMemo(() => {
    if (!value) return undefined;
    return value.placeId;
  }, [value]);

  // Create options that include the current value if it's not in suggestions
  const allOptions = useMemo(() => {
    if (!value) return options;

    // Check if current value is already in options
    const valueInOptions = options.some((opt) => opt.value === value.placeId);
    if (valueInOptions) return options;

    // Add current value as an option so it displays correctly
    const currentOption: AutocompleteOption = {
      value: value.placeId,
      label: `${value.city}, ${value.country}`,
      icon: <MapPin className="text-muted-foreground h-4 w-4" />,
    };

    return [currentOption, ...options];
  }, [options, value]);

  // Handle auto-detect location using browser geolocation with IP fallback
  const handleAutoDetect = useCallback(async () => {
    // Try browser geolocation first, then reverse geocode, then IP fallback
    const tryIpFallback = async () => {
      try {
        const ipLocation = await getIpLocation();

        const result: CityAutocompleteResult = {
          city: ipLocation.city,
          country: ipLocation.country,
          countryCode: ipLocation.countryCode,
          placeId:
            `ip_${ipLocation.city}_${ipLocation.countryCode}`.toLowerCase(),
          latitude: ipLocation.latitude,
          longitude: ipLocation.longitude,
        };

        onChange(result);

        toast.success(t('home:locationAutocomplete.approximateDetected'), {
          description: t(
            'home:locationAutocomplete.approximateDetectedDescription',
            { city: ipLocation.city, country: ipLocation.country },
          ),
        });
        return true;
      } catch {
        return false;
      }
    };

    try {
      // Get current coordinates from browser
      const coords = await getCurrentLocation();

      // Only attempt reverse geocode if Google Maps API is loaded
      if (isLoaded) {
        try {
          const location = await reverseGeocode(
            coords.latitude,
            coords.longitude,
          );

          onChange(location);

          toast.success(t('home:locationAutocomplete.detected'), {
            description: t('home:locationAutocomplete.detectedDescription', {
              city: location.city,
              country: location.country,
            }),
          });
          return;
        } catch {
          // Reverse geocode failed (e.g., Geocoder not available, no results)
          // Fall through to IP fallback
        }
      }

      // If reverse geocode was skipped or failed, try IP fallback
      const ipSuccess = await tryIpFallback();
      if (!ipSuccess) {
        toast.error(t('home:locationAutocomplete.detectionFailed'), {
          description: t(
            'home:locationAutocomplete.detectionFailedDescription',
          ),
        });
      }
    } catch {
      // Browser geolocation failed entirely — try IP fallback for ALL error types
      const ipSuccess = await tryIpFallback();
      if (!ipSuccess) {
        toast.error(t('home:locationAutocomplete.detectionFailed'), {
          description: t(
            'home:locationAutocomplete.detectionFailedDescription',
          ),
        });
      }
    }
  }, [getCurrentLocation, getIpLocation, onChange, isLoaded, t]);

  // Show API error if present
  const displayError = apiError
    ? t('home:locationAutocomplete.loadError')
    : error;

  // Auto-detect ("locate me") control, embedded at the trailing edge of the
  // input. Icon-only button; gated on browser geolocation support.
  const locateAction = isSupported ? (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={handleAutoDetect}
      disabled={isGeolocating}
      className="h-7 w-7 shrink-0"
      aria-label={t('home:locationAutocomplete.autoDetect')}
      title={t('home:locationAutocomplete.autoDetect')}
    >
      {isGeolocating ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Navigation className="h-4 w-4" />
      )}
    </Button>
  ) : undefined;

  return (
    <Autocomplete
      value={displayValue}
      onChange={handleSelect}
      options={allOptions}
      placeholder={resolvedPlaceholder}
      label={resolvedLabel}
      error={displayError}
      required={required}
      disabled={isLoadingApi || !!apiError}
      loading={isLoadingPredictions || isLoadingDetails}
      onInputChange={handleInputChange}
      // Options are the authoritative Google Places result for the current
      // query (driven by onInputChange). Skip the Autocomplete's built-in
      // client-side substring filter — otherwise diacritics or partial/typo'd
      // queries (e.g. "zurie" → "Zürich") fail the literal substring match and
      // every suggestion is hidden, collapsing the dropdown the instant
      // results arrive.
      serverFiltered
      debounceMs={300}
      minChars={2}
      emptyMessage={t('home:locationAutocomplete.emptyMessage')}
      loadingMessage={t('home:locationAutocomplete.loadingMessage')}
      trailingAction={locateAction}
    />
  );
}
